import type { CSSProperties } from "react"
import { BrandInkPreloader } from "@/widgets/BrandInkPreloader"

type LoadingBlockProps = {
  /** Shown under the stroke (stack) or to the right of stroke (row); omit for stroke-only (e.g. infinite-scroll sentinel). */
  message?: string
  /** page: large area + screen-reader announce; section: card/list block; inline: compact strip */
  variant?: "page" | "section" | "inline"
  /** stack: stroke above caption; row: stroke and caption on one line (lists, panels). */
  layout?: "stack" | "row"
  className?: string
  /** Merged onto the outer wrapper (e.g. minHeight overrides for auth gates). */
  rootStyle?: CSSProperties
}

/**
 * Project-wide loading UI built on {@link BrandInkPreloader}.
 */
export const LoadingBlock = ({
  message,
  variant = "section",
  layout = "stack",
  className = "",
  rootStyle,
}: LoadingBlockProps) => {
  const isPage = variant === "page"
  const isInline = variant === "inline"
  const size = isPage ? "default" : "compact"
  const row = layout === "row" && variant === "section"

  const shell = isPage
    ? `min-h-[50vh] flex flex-col items-center justify-center bg-white px-4 ${className}`
    : isInline
      ? `flex flex-col items-center justify-center gap-2 ${className}`
      : row
        ? `flex flex-row items-center justify-center gap-3 px-4 text-gray-600 ${className}`
        : `flex flex-col items-center justify-center gap-3 py-10 px-4 text-gray-600 ${className}`

  if (isPage) {
    return (
      <div className={shell} style={rootStyle}>
        <BrandInkPreloader announce size={size} label={message ?? ""} />
      </div>
    )
  }

  if (isInline && !message) {
    return (
      <div className={shell} style={rootStyle} aria-hidden>
        <BrandInkPreloader announce={false} size="compact" />
      </div>
    )
  }

  if (row && message) {
    return (
      <div className={shell} style={rootStyle} role="status" aria-live="polite">
        <BrandInkPreloader announce={false} size="compact" />
        <span className="text-sm font-medium text-gray-600">{message}</span>
      </div>
    )
  }

  return (
    <div className={shell} style={rootStyle} role="status" aria-live="polite">
      <BrandInkPreloader announce={false} size="compact" label={message} />
    </div>
  )
}

type InlineLoadingDotsProps = {
  className?: string
  /**
   * `default` — рядом с текстом в кнопках (фиксированный «слот», без раздувания flex).
   * `compact` — суффикс в полях ввода (меньше).
   */
  variant?: "default" | "compact"
}

/**
 * Встроенный прелоадер: `transform: scale` не уменьшает разметку, поэтому внутри задан
 * фиксированный контейнер + overflow, чтобы кнопки/ряды не прыгали по высоте.
 */
export const InlineLoadingDots = ({ className = "", variant = "default" }: InlineLoadingDotsProps) => {
  // Слот по высоте ≈ line-box `text-sm` (leading 1.25rem), иначе flex-кнопка растёт до max(текст, иконка).
  const shell =
    variant === "compact"
      ? "inline-flex size-4 shrink-0 items-center justify-center overflow-hidden align-middle leading-none"
      : "inline-flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden align-middle leading-none"
  const innerScale = variant === "compact" ? "scale-[0.286]" : "scale-[0.357]"

  return (
    <span className={`${shell} ${className}`} aria-hidden>
      <span className={`inline-flex origin-center ${innerScale} leading-none`}>
        <BrandInkPreloader announce={false} size="compact" />
      </span>
    </span>
  )
}
